import { useState, useEffect } from 'react';
import { Plus, X, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import api from '../api';

const INP = {
  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
  padding: '9px 12px', color: 'hsl(0,0%,90%)',
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4, display: 'block',
};
const SECTION = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)',
  textTransform: 'uppercase', letterSpacing: '0.15em',
  marginBottom: 10, borderBottom: '1px solid hsl(0,0%,12%)', paddingBottom: 6,
};

const TDS_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: '194C', label: '194C — Contractors' },
  { value: '194J', label: '194J — Professional' },
  { value: '194I', label: '194I — Rent' },
];

const PO_OPTIONS = [
  { value: 'optional', label: 'Optional' },
  { value: 'yes', label: 'Required' },
  { value: 'no', label: 'Not Required' },
];

const EMPTY = {
  name: '', code: '', gstin: '', pan: '',
  city: '', state: '', pincode: '',
  contact_name: '', contact_phone: '', contact_email: '',
  bank_account_no: '', bank_ifsc: '', bank_name: '',
  payment_terms: 'Net 30',
  service_category: '',
  sac_code: '',
  tds_section: 'none',
  po_required: 'optional',
};

function ServiceProviderModal({ provider, onClose, onSaved }) {
  const isEdit = !!provider?.id;
  const [form, setForm] = useState(isEdit ? {
    name: provider.name || '',
    code: provider.code || '',
    gstin: provider.gstin || '',
    pan: provider.pan || '',
    city: provider.registered_address?.city || provider.city || '',
    state: provider.registered_address?.state || provider.state || '',
    pincode: provider.registered_address?.pincode || '',
    contact_name: provider.contact_name || '',
    contact_phone: provider.contact_phone || '',
    contact_email: provider.contact_email || '',
    bank_account_no: provider.bank_account_no || '',
    bank_ifsc: provider.bank_ifsc || '',
    bank_name: provider.bank_name || '',
    payment_terms: provider.payment_terms || 'Net 30',
    service_category: provider.service_category || '',
    sac_code: provider.sac_code || '',
    tds_section: provider.tds_section || 'none',
    po_required: provider.po_required || 'optional',
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      registered_address: { city: form.city, state: form.state, pincode: form.pincode },
    };
    try {
      if (isEdit) { await api.put(`/service-providers/${provider.id}`, payload); }
      else { await api.post('/service-providers', payload); }
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 600, padding: 24, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>
            {isEdit ? 'Edit Service Provider' : 'Add Service Provider'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Basic Info</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Provider Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)}
                  required style={INP} data-testid="provider-name" /></div>
              <div><label style={LBL}>Code</label>
                <input value={form.code} onChange={e => set('code', e.target.value)}
                  placeholder="SVC-XXX" style={INP} /></div>
              <div><label style={LBL}>GSTIN</label>
                <input value={form.gstin} onChange={e => set('gstin', e.target.value)}
                  placeholder="22AAAAA0000A1Z5" style={INP} /></div>
              <div><label style={LBL}>PAN</label>
                <input value={form.pan} onChange={e => set('pan', e.target.value)}
                  placeholder="AAAAA0000A" style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Service Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>Service Category</label>
                <input value={form.service_category} onChange={e => set('service_category', e.target.value)}
                  placeholder="e.g. IT Services, Maintenance" style={INP} /></div>
              <div><label style={LBL}>SAC Code</label>
                <input value={form.sac_code} onChange={e => set('sac_code', e.target.value)}
                  placeholder="e.g. 998314" style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>TDS Section</label>
                <select value={form.tds_section} onChange={e => set('tds_section', e.target.value)}
                  style={{ ...INP, appearance: 'none' }}>
                  {TDS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div><label style={LBL}>PO Required</label>
                <select value={form.po_required} onChange={e => set('po_required', e.target.value)}
                  style={{ ...INP, appearance: 'none' }}>
                  {PO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Address &amp; Contact</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>City</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>State</label>
                <input value={form.state} onChange={e => set('state', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Pincode</label>
                <input value={form.pincode} onChange={e => set('pincode', e.target.value)} style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Contact Name</label>
                <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Phone</label>
                <input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Email</label>
                <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Bank &amp; Payment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>Bank Name</label>
                <input value={form.bank_name} onChange={e => set('bank_name', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Payment Terms</label>
                <input value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)}
                  placeholder="Net 30" style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Account No</label>
                <input value={form.bank_account_no} onChange={e => set('bank_account_no', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>IFSC</label>
                <input value={form.bank_ifsc} onChange={e => set('bank_ifsc', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-provider-btn">
              {saving ? 'Saving...' : isEdit ? 'Update Provider' : 'Add Provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ServiceProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProvider, setEditProvider] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/service-providers', { params: { search } });
      setProviders(r.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (p) => {
    await api.patch(`/service-providers/${p.id}/status`, { status: p.status === 'active' ? 'inactive' : 'active' });
    load();
  };

  const tdsLabel = (val) => {
    const opt = TDS_OPTIONS.find(o => o.value === val);
    return opt ? opt.value === 'none' ? '—' : opt.value : '—';
  };

  const poLabel = (val) => {
    if (val === 'yes') return 'Required';
    if (val === 'no') return 'Not Req.';
    return 'Optional';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
            Service Providers
          </h1>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)', marginTop: 4 }}>
            Contractors, IT, utilities &amp; other services
          </p>
        </div>
        <button onClick={() => { setEditProvider(null); setShowModal(true); }}
          className="btn-primary" data-testid="add-provider-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
          <Plus size={14} /> Add Provider
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 320 }}>
        <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%',
          transform: 'translateY(-50%)', color: 'hsl(0,0%,40%)', pointerEvents: 'none' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, GSTIN..."
          data-testid="provider-search"
          style={{ ...INP, paddingLeft: 30 }} />
      </div>

      <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 100px 160px 140px 90px 80px 110px',
          background: 'hsl(0,0%,8%)', padding: '8px 12px', borderBottom: '1px solid hsl(0,0%,14%)' }}>
          {['Name','Code','GSTIN','Contact','Category','TDS','Actions'].map(h => (
            <span key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
              color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</span>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'hsl(0,0%,40%)' }}>Loading...</div>
        ) : providers.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'hsl(0,0%,35%)' }}>No service providers found</div>
        ) : providers.map(p => (
          <div key={p.id} data-testid="provider-row"
            style={{ display: 'grid', gridTemplateColumns: '200px 100px 160px 140px 90px 80px 110px',
              padding: '10px 12px', borderBottom: '1px solid hsl(0,0%,10%)', alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                color: p.status === 'inactive' ? 'hsl(0,0%,40%)' : 'hsl(0,0%,90%)' }}>{p.name}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,35%)', marginTop: 2 }}>
                {[p.registered_address?.city || p.city, p.registered_address?.state || p.state].filter(Boolean).join(', ')}
              </div>
            </div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,55%)' }}>{p.code || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,60%)' }}>{p.gstin || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,70%)' }}>{p.contact_name || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: 'hsl(0,0%,55%)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.service_category || '—'}
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: p.tds_section && p.tds_section !== 'none' ? 'hsl(197,80%,55%)' : 'hsl(0,0%,40%)' }}>
              {tdsLabel(p.tds_section)}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => { setEditProvider(p); setShowModal(true); }}
                data-testid="edit-provider-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: 'hsl(0,0%,50%)', padding: 0 }}>
                <PencilSimple size={13} />
              </button>
              <button onClick={() => toggleStatus(p)}
                data-testid="toggle-provider-status-btn"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                  padding: '2px 8px', cursor: 'pointer',
                  background: p.status === 'active' ? 'hsl(0,0%,12%)' : 'hsl(0,0%,10%)',
                  border: `1px solid ${p.status === 'active' ? 'hsl(142,50%,35%)' : 'hsl(0,0%,25%)'}`,
                  color: p.status === 'active' ? 'hsl(142,60%,50%)' : 'hsl(0,0%,45%)' }}>
                {p.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <ServiceProviderModal
          provider={editProvider}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
